import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

/* ── Shared table pager ───────────────────────────────────────────────────────
 * Every paginated table in the app used its own prev/next pair, which meant the
 * only way to reach page 14 was to click ถัดไป thirteen times. This replaces all
 * of them: numbered pages you can click directly, plus first/last jumps.
 *
 * With many pages the numbers window down to `1 … 6 [7] 8 … 214`, and the **…
 * is a button** — clicking it opens an inline "go to page" input, so any page is
 * two clicks away no matter how long the list is. That input is why there is no
 * separate ไปหน้า box: several of these footers (the recall drawer, the stock
 * ledgers) have no room for one.
 *
 *   page       1-based current page
 *   pageTotal  total number of pages; renders nothing when < 2 (every call site
 *              previously guarded with `pageTotal > 1` itself)
 *   onChange   (nextPage) => void — already clamped to 1..pageTotal, and never
 *              fired for the page you are already on
 *   disabled   whole control greyed out (used while a fetch is in flight)
 *   size       'md' (default) | 'sm' for tight footers such as drawers
 *   span       how many numbers to show either side of the current page
 */
export default function Pager({
  page, pageTotal, onChange,
  disabled = false, size = 'md', span = 1, className = '',
}) {
  const total = Math.max(1, Number(pageTotal) || 1)
  const cur = Math.min(total, Math.max(1, Number(page) || 1))
  const [jumpAt, setJumpAt] = useState(null)   // index of the … being edited
  const [draft, setDraft] = useState('')

  // Close the jump input whenever the page changes underneath it (a click
  // elsewhere, a filter change) so it can't linger over a different window.
  useEffect(() => { setJumpAt(null) }, [cur, total])

  if (total < 2) return null

  const go = (n) => {
    const next = Math.min(total, Math.max(1, Math.round(Number(n) || 0)))
    setJumpAt(null)
    if (next !== cur) onChange?.(next)
  }
  // Clearing the draft first makes this idempotent: pressing Enter unmounts the
  // input, and a browser that also fires blur on that unmount would otherwise
  // re-submit the same number.
  const commitJump = () => {
    const v = draft.trim()
    setDraft('')
    if (v !== '') go(v); else setJumpAt(null)
  }

  const sm = size === 'sm'
  const base = `inline-flex items-center justify-center rounded-lg border tabular-nums transition-colors disabled:opacity-40 disabled:pointer-events-none ${
    sm ? 'min-w-[24px] h-6 px-1.5 text-[11.5px]' : 'min-w-[28px] h-7 px-2 text-[12.5px]'}`
  const quiet = `${base} border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-600`
  const active = `${base} border-brand-500 bg-brand-500 text-white font-semibold hover:bg-brand-600 hover:border-brand-600`
  const icon = sm ? 12 : 14

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button type="button" className={quiet} disabled={disabled || cur <= 1}
        onClick={() => go(1)} title="หน้าแรก" aria-label="หน้าแรก"><ChevronsLeft size={icon} /></button>
      <button type="button" className={quiet} disabled={disabled || cur <= 1}
        onClick={() => go(cur - 1)} title="ก่อนหน้า" aria-label="ก่อนหน้า"><ChevronLeft size={icon} /></button>

      {pageWindow(cur, total, span).map((it, i) => (
        it !== GAP ? (
          <button key={`p${it}`} type="button" disabled={disabled}
            className={it === cur ? active : quiet}
            aria-current={it === cur ? 'page' : undefined}
            onClick={() => go(it)}>{it}</button>
        ) : jumpAt === i ? (
          <input key={`j${i}`} type="number" min={1} max={total} autoFocus
            value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitJump() }
              if (e.key === 'Escape') setJumpAt(null)
            }}
            onBlur={commitJump}
            placeholder={String(cur)}
            className={`rounded-lg border border-brand-400 text-center text-slate-700 outline-none tabular-nums ${
              sm ? 'w-11 h-6 text-[11.5px]' : 'w-12 h-7 text-[12.5px]'}`} />
        ) : (
          <button key={`g${i}`} type="button" disabled={disabled}
            onClick={() => { setDraft(''); setJumpAt(i) }}
            title="ไปยังหน้า…" aria-label="ไปยังหน้า"
            className={`${quiet} border-transparent hover:bg-slate-100 text-slate-400`}>…</button>
        )
      ))}

      <button type="button" className={quiet} disabled={disabled || cur >= total}
        onClick={() => go(cur + 1)} title="ถัดไป" aria-label="ถัดไป"><ChevronRight size={icon} /></button>
      <button type="button" className={quiet} disabled={disabled || cur >= total}
        onClick={() => go(total)} title="หน้าสุดท้าย" aria-label="หน้าสุดท้าย"><ChevronsRight size={icon} /></button>
    </div>
  )
}

const GAP = Symbol('gap')

/* First page, last page, and a ±span window around the current one. A gap of
 * exactly one page renders as that page rather than a … — swapping a reachable
 * number for an ellipsis would be a strictly worse control. */
function pageWindow(cur, total, span) {
  const keep = new Set([1, total])
  for (let i = cur - span; i <= cur + span; i++) if (i >= 1 && i <= total) keep.add(i)
  const nums = [...keep].sort((a, b) => a - b)
  const out = []
  for (let i = 0; i < nums.length; i++) {
    if (i > 0) {
      const gap = nums[i] - nums[i - 1]
      if (gap === 2) out.push(nums[i] - 1)
      else if (gap > 2) out.push(GAP)
    }
    out.push(nums[i])
  }
  return out
}
